<template>
    <div>
        <v-card class="soli-round">
            <v-container class="px-3" fluid>
                <v-data-table
                    ref="table"
                    :headers="headers"
                    :items="people || []"
                    disable-pagination
                    hide-default-footer
                    show-select
                    v-model="selectedPeople"
                    mobile-breakpoint="0"
                    :search="search"
                    :loading="!people"
                    :groupBy="groupBy"
                    @current-items="currentItems = $event"
                    @click:row="$log($event.id)"
                >
                    <template v-slot:top>
                        <div class="font-semibold soli-grey text-lg py-2 px-4">
                            {{
                                `${
                                    selectedPeople.length
                                        ? `Selected: ${selectedPeople.length} `
                                        : `Total: ${currentItems?.length ?? ""}`
                                }`
                            }}
                        </div>
                    </template>

                    <template
                        v-slot:[`group.header`]="{ items, isOpen, toggle }"
                    >
                        <th
                            v-if="items[0].family_id"
                            :colspan="headers.length + 1"
                        >
                            <div class="flex font-weight-regular">
                                <div class="flex-col align-self-center mr-4">
                                    <v-icon @click="toggle"
                                        >{{ isOpen ? "remove" : "add" }}
                                    </v-icon>
                                </div>
                                <v-list-item-subtitle
                                    class="flex align-self-center font-weight-bold soli-grey"
                                >
                                    {{
                                        `${items[0].last_name} Family`
                                    }}</v-list-item-subtitle
                                >
                            </div>
                        </th>
                    </template>

                    <template v-slot:[`item.name`]="{ item }">
                        <SoliListAvatar
                            :avatar="
                                item.profile_photo_path
                                    ? `/api/image-service/40x40/${item.profile_photo_path}`
                                    : item.profile_photo_url
                            "
                            :title="item.name"
                            :subtitle="item.email"
                            :href="`/profile/${item.id}/account`"
                        />
                    </template>

                    <template
                        v-if="['students', 'prospects'].includes(role)"
                        v-slot:[`item.purchaseOptions`]="{ item }"
                    >
                        <v-list-item-subtitle
                            class="caption"
                            style="max-width: 35vw"
                        >
                            {{ item.purchaseOptions }}
                        </v-list-item-subtitle>
                    </template>

                    <template
                        v-if="
                            can('Manage memberships') &&
                            ['students', 'prospects'].includes(role)
                        "
                        v-slot:[`item.membershipPrices`]="{ item }"
                    >
                        <v-list-item-subtitle class="caption">
                            {{ item.membershipPrices }}
                        </v-list-item-subtitle>
                    </template>

                    <template
                        v-if="['students', 'prospects'].includes(role)"
                        v-slot:[`item.tags`]="{ item }"
                    >
                        <div class="flex gap-1">
                            <v-chip
                                v-for="tag in item.tags"
                                :key="tag.id"
                                :color="tag.color"
                                x-small
                                class="white--text font-weight-bold text-capitalize"
                                >{{ tag.name }}</v-chip
                            >
                        </div>
                    </template>

                    <template
                        v-if="role == 'teachers'"
                        v-slot:[`item.instruments`]="{ item }"
                    >
                        <v-list-item-subtitle
                            class="caption"
                            style="max-width: 20vw"
                        >
                            {{ item.instruments }}
                        </v-list-item-subtitle>
                    </template>

                    <template
                        v-if="role == 'teachers'"
                        v-slot:[`item.locations`]="{ item }"
                    >
                        <v-list-item-subtitle
                            class="caption"
                            style="max-width: 20vw"
                        >
                            {{ item.locations }}
                        </v-list-item-subtitle>
                    </template>

                    <template
                        v-if="role == 'teachers'"
                        v-slot:[`item.certification_level`]="{ item }"
                    >
                        <v-list-item-subtitle class="caption">
                            {{ item.certification_level }}
                        </v-list-item-subtitle>
                    </template>

                    <template
                        v-if="role == 'staff'"
                        v-slot:[`item.staffRole`]="{ item }"
                    >
                        <v-list-item-subtitle class="caption">
                            {{ item.staffRole }}
                        </v-list-item-subtitle>
                    </template>

                    <template v-slot:[`item.created_at`]="{ item }">
                        <v-list-item-subtitle class="caption">
                            {{ globalMomentDate(item.created_at) }}
                        </v-list-item-subtitle>
                    </template>

                    <template
                        v-if="role == 'teachers'"
                        v-slot:[`item.capacity`]="{ item }"
                    >
                        <v-list-item-subtitle
                            v-if="!capacityLoading"
                            class="caption"
                        >
                            {{ (item.capacity * 100).toFixed(2) }}%
                        </v-list-item-subtitle>
                        <v-progress-circular
                            size="25"
                            v-else
                            indeterminate
                            color="primary"
                        ></v-progress-circular>
                    </template>

                    <template
                        v-if="role == 'teachers'"
                        v-slot:[`item.retention`]="{ item }"
                    >
                        <v-list-item-subtitle
                            v-if="!retentionLoading"
                            :class="[item.retention.color, 'caption']"
                        >
                            {{
                                (Number(item.retention.value) * 100).toFixed(2)
                            }}%
                            <v-icon small :class="item.retention.color">{{
                                item.retention.icon
                            }}</v-icon>
                        </v-list-item-subtitle>
                        <v-progress-circular
                            size="25"
                            v-else
                            indeterminate
                            color="primary"
                        ></v-progress-circular>
                    </template>

                    <template
                        v-if="role == 'teachers'"
                        v-slot:[`item.hours`]="{ item }"
                    >
                        <v-list-item-subtitle
                            v-if="!hoursLoading"
                            class="caption"
                        >
                            {{ item.hours }}
                        </v-list-item-subtitle>
                        <v-progress-circular
                            size="25"
                            v-else
                            indeterminate
                            color="primary"
                        ></v-progress-circular>
                    </template>

                    <template
                        v-if="role == 'students'"
                        v-slot:[`item.reschedules`]="{ item }"
                    >
                        <v-list-item-subtitle
                            class="caption"
                            style="max-width: 35vw"
                        >
                            {{ item.reschedules }}
                        </v-list-item-subtitle>
                    </template>
                </v-data-table>
            </v-container>
        </v-card>
    </div>
</template>

<script>
import { Link } from "@inertiajs/vue2";
import globalMoment from "../../Mixins/GlobalMoment.js";
import Stats from "../Reports/Stats.vue";

export default {
    name: "UserList",
    mixins: [globalMoment],
    props: {
        role: {
            type: String,
            default: null,
        },
        people: {
            type: Array,
            default: null,
        },
        headers: {
            type: Array,
            default: () => [],
        },
        search: {
            type: String,
            default: "",
        },
        groupBy: {
            type: String,
            default: null,
        },
    },
    components: {
        Link,
        Stats,
    },
    data() {
        return {
            capacityLoading: false,
            retentionLoading: false,
            hoursLoading: false,
            selectedPeople: [],
            currentItems: null,
        };
    },
    watch: {
        selectedPeople(val) {
            this.$emit("selectedPeople", val);
        },
        people: {
            handler(val) {
                if (val && this.role == "teachers") {
                    this.getCapacity();
                    this.getRetention();
                    this.getHours();
                }
            },
            immediate: true,
        },
    },
    methods: {
        getCapacity() {
            this.capacityLoading = true;
            axios
                .get("/api/users/capacity", {
                    params: {
                        teachers: this.people.map((e) => e.id),
                    },
                })
                .then((resp) => {
                    this.people.forEach((e, idx) => {
                        e.capacity = Number(resp.data[idx]);
                    });
                    this.capacityLoading = false;
                })
                .catch((error) => {
                    console.log(error);
                    this.capacityLoading = false;
                });
        },
        getRetention() {
            this.retentionLoading = true;
            axios
                .get("/api/users/retention", {
                    params: {
                        teachers: this.people.map((e) => e.id),
                    },
                })
                .then((resp) => {
                    this.people.forEach((e, idx) => {
                        e.retention = resp.data[idx];
                    });
                    this.retentionLoading = false;
                })
                .catch((error) => {
                    console.log(error);
                    this.retentionLoading = false;
                });
        },
        getHours() {
            this.hoursLoading = true;
            axios
                .get("/api/users/teacher-hours", {
                    params: {
                        teachers: this.people.map((e) => e.id),
                    },
                })
                .then((resp) => {
                    this.people.forEach((e, idx) => {
                        e.hours = resp.data[idx];
                    });
                    this.hoursLoading = false;
                })
                .catch((error) => {
                    console.log(error);
                    this.hoursLoading = false;
                });
        },
    },
};
</script>
